import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTexture } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { DoubleSide, Object3D, SRGBColorSpace } from 'three';
import type { InstancedMesh } from 'three';
import { DRIVING_TRACK_INNER_RADIUS } from '../model/stageGround';
import { SHOWROOM_LOGO_URL } from './brandAssets';

const SIGN_COUNT = 24;
const SIGN_RADIUS = DRIVING_TRACK_INNER_RADIUS - 5;
/** 원본은 정사각형 로고입니다. 차량보다 지나치게 커 보이지 않도록 3m로 제한합니다. */
const SIGN_SIZE = 3;
const SIGN_CENTER_Y = 1.95;
const POST_HEIGHT = 0.62;
const POST_WIDTH = 0.1;
const LOGO_ALPHA_CUTOFF = 0.08;

/**
 * 외곽 트랙 안쪽에 같은 간격으로 놓는 양면 브랜드 표지판입니다.
 * 판과 기둥을 각각 한 InstancedMesh로 그려 표지판 수가 늘어도 draw call은 두 개입니다.
 */
export function TrackBranding() {
  const signsRef = useRef<InstancedMesh | null>(null);
  const postsRef = useRef<InstancedMesh | null>(null);
  const logo = useTexture(SHOWROOM_LOGO_URL);
  const maxAnisotropy = useThree((state) => state.gl.capabilities.getMaxAnisotropy());

  useEffect(() => {
    logo.colorSpace = SRGBColorSpace;
    logo.anisotropy = Math.min(8, maxAnisotropy);
    logo.needsUpdate = true;
  }, [logo, maxAnisotropy]);

  useLayoutEffect(() => {
    const signs = signsRef.current;
    const posts = postsRef.current;
    if (signs === null || posts === null) {
      return;
    }

    const signTransform = new Object3D();
    const postTransform = new Object3D();
    for (let index = 0; index < SIGN_COUNT; index += 1) {
      // 출발선 바로 옆을 비워 시야와 체커 라인을 가리지 않도록 반 간격만큼 돌립니다.
      const angle = ((index + 0.5) / SIGN_COUNT) * Math.PI * 2;
      const radialX = Math.cos(angle);
      const radialZ = Math.sin(angle);
      const x = radialX * SIGN_RADIUS;
      const z = radialZ * SIGN_RADIUS;

      signTransform.position.set(x, SIGN_CENTER_Y, z);
      // PlaneGeometry의 +Z 면이 트랙 바깥(차량이 달리는 방향)을 향합니다.
      signTransform.lookAt(radialX * (SIGN_RADIUS + 10), SIGN_CENTER_Y, radialZ * (SIGN_RADIUS + 10));
      signTransform.updateMatrix();
      signs.setMatrixAt(index, signTransform.matrix);

      postTransform.position.set(x, POST_HEIGHT / 2, z);
      postTransform.rotation.set(0, -angle, 0);
      postTransform.updateMatrix();
      posts.setMatrixAt(index, postTransform.matrix);
    }
    signs.instanceMatrix.needsUpdate = true;
    posts.instanceMatrix.needsUpdate = true;
    signs.computeBoundingSphere();
    posts.computeBoundingSphere();
  }, []);

  return (
    <>
      <instancedMesh ref={postsRef} args={[undefined, undefined, SIGN_COUNT]}>
        <boxGeometry args={[POST_WIDTH, POST_HEIGHT, POST_WIDTH]} />
        <meshStandardMaterial color="#545a60" roughness={0.62} metalness={0.5} />
      </instancedMesh>
      <instancedMesh ref={signsRef} args={[undefined, undefined, SIGN_COUNT]}>
        <planeGeometry args={[SIGN_SIZE, SIGN_SIZE]} />
        <meshStandardMaterial
          map={logo}
          color="#ffffff"
          roughness={0.48}
          metalness={0.02}
          side={DoubleSide}
          // WebP의 투명 픽셀 RGB를 그리면 회색 판과 검은 막대가 드러납니다.
          // 알파가 거의 0인 픽셀을 폐기하고 가장자리만 MSAA로 부드럽게 처리합니다.
          alphaTest={LOGO_ALPHA_CUTOFF}
          alphaToCoverage
        />
      </instancedMesh>
    </>
  );
}
